import React, { useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import { updatePassword } from "../../services/userService"
import SectionHeader from "../ui/SectionHeader"
import { FiLock } from "react-icons/fi"

const inputStyle =
  "w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"

// Muss der Backend-Regel entsprechen: mind. 8 Zeichen, 1 Großbuchstabe, 1 Zahl, 1 Sonderzeichen
const passwordPattern =
  /^(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).{8,}$/

export default function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const clearMessages = () => {
    setError("")
    setSuccess("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess("")

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill in all password fields.")
      return
    }
    if (newPassword !== confirmPassword) {
      setError("The new passwords do not match.")
      return
    }
    if (!passwordPattern.test(newPassword)) {
      setError(
        "Password must be at least 8 characters and include an uppercase letter, a number and a special character."
      )
      return
    }

    setSaving(true)
    try {
      await updatePassword({ currentPassword, newPassword, confirmPassword })
      setSuccess("Your password has been updated.")
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err: any) {
      setError(
        err?.message || "Your password could not be updated. Please try again."
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
    >
      <SectionHeader
        icon={<FiLock size={20} />}
        title="Change password"
        subtitle="Use a strong password you don't use elsewhere."
      />

      <div className="flex flex-col gap-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Current password *
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value)
              clearMessages()
            }}
            className={inputStyle}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              New password *
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                clearMessages()
              }}
              className={inputStyle}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Confirm new password *
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                clearMessages()
              }}
              className={inputStyle}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          At least 8 characters, including an uppercase letter, a number and a
          special character.
        </p>
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="mt-5">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-dantzOrange px-6 py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
        >
          {saving ? <SpinnerText>Updating…</SpinnerText> : "Update password"}
        </button>
      </div>
    </form>
  )
}
